// FILE: usePrimaryNewThread.ts
// Purpose: Single source of truth for the "New thread" entry points — which
//          project a fresh thread lands in, and whether the action is offered
//          at all.
// Layer: Web hooks
// Exports: usePrimaryNewThread
//
// The sidebar button and the home page both need this decision, and it is the
// kind of rule that rots when it is written twice: the project fallback and the
// permission gate have to agree, or one surface offers an action the other one
// hides and the server refuses.

import { type ProjectId } from "@taskin-code/contracts";
import { useCallback } from "react";

import { useHasAnyProjectPermission } from "../auth/authStore";
import { openNewThreadDialog } from "../newThreadDialogStore";
import { useStore } from "../store";

/**
 * Why the action did or did not run. Callers that have somewhere better to send
 * the user than a dead button — the sidebar sends them to "add project" — need
 * to tell the two failures apart.
 */
export type PrimaryNewThreadOutcome = "opened" | "no-project" | "not-permitted";

interface UsePrimaryNewThreadOptions {
  /**
   * Project to start in. Callers with a notion of "where the user is" pass it;
   * `null`/absent falls back to the first project, same as before.
   */
  readonly preferredProjectId?: ProjectId | null;
}

export interface PrimaryNewThread {
  /** Project the dialog would pre-select, or `null` if there is none to pick. */
  readonly targetProjectId: ProjectId | null;
  /** Whether `startNewThread` would reach the dialog. */
  readonly canStartNewThread: boolean;
  readonly startNewThread: () => PrimaryNewThreadOutcome;
}

export function usePrimaryNewThread(options: UsePrimaryNewThreadOptions = {}): PrimaryNewThread {
  const { preferredProjectId = null } = options;
  const firstProjectId = useStore((store) => store.projects[0]?.id ?? null);
  // The server refuses `thread.create` without it, so a surface offering the
  // action to a user who holds it nowhere is offering a guaranteed 403.
  const mayCreateThread = useHasAnyProjectPermission("p.thread.create");
  const targetProjectId = preferredProjectId ?? firstProjectId;

  const startNewThread = useCallback((): PrimaryNewThreadOutcome => {
    if (!mayCreateThread) return "not-permitted";
    if (!targetProjectId) return "no-project";
    openNewThreadDialog({ preferredProjectId: targetProjectId });
    return "opened";
  }, [mayCreateThread, targetProjectId]);

  return {
    targetProjectId,
    canStartNewThread: mayCreateThread && targetProjectId !== null,
    startNewThread,
  };
}
